import { measureValue } from "./format.js";

const START_ID = "african_elephant";
const FIRST_CHALLENGE_ID = "grand_piano";
const DIGITAL_START_ID = "minecraft_file";
const DIGITAL_FIRST_CHALLENGE_ID = "fortnite_file";

export function normalizeItems(items, locale = "en") {
  const seen = new Set();
  return (Array.isArray(items) ? items : [])
    .map((item) => {
      const explicitType = String(item.measureType || "").trim();
      const measureType = explicitType === "digital" ? "digital" : explicitType === "mass" ? "mass" : item.sizeMb !== undefined ? "digital" : "mass";
      const localized = item.i18n?.[locale] || {};
      return {
        id: String(item.id || "").trim(),
        name: String(localized.name || item.name || "").trim(),
        emoji: String(item.emoji || "📦").trim() || "📦",
        weightKg: Number(item.weightKg),
        sizeMb: Number(item.sizeMb),
        measureType,
        category: String(item.category || "").trim(),
        url: String(item.url || "").trim(),
        fact: String(localized.fact || item.fact || "").trim()
      };
    })
    .filter((item) => {
      const value = measureValue(item);
      if (!item.id || !item.name || !item.category || !Number.isFinite(value) || value <= 0) {
        return false;
      }
      if (seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });
}

export function createGame(items, preferredMeasureType = "mass", locale = "en") {
  const normalized = normalizeItems(items, locale);
  const preferred = normalized.filter((item) => item.measureType === preferredMeasureType);
  const pool = preferred.length >= 5 ? preferred : normalized.filter((item) => item.measureType === "mass");
  const startId = preferredMeasureType === "digital" ? DIGITAL_START_ID : START_ID;
  const firstId = preferredMeasureType === "digital" ? DIGITAL_FIRST_CHALLENGE_ID : FIRST_CHALLENGE_ID;
  const start = pool.find((item) => item.id === startId) || pool[0] || normalized[0];
  const first = pool.find((item) => item.id === firstId && item.id !== start.id);
  return {
    items: pool,
    baseline: start,
    challenge: first || pickChallenge(pool, start, new Set([start.id]), 0),
    used: new Set([start.id, first?.id].filter(Boolean)),
    lives: 3,
    streak: 0,
    history: [],
    reveal: null,
    gameOver: false,
    round: 0
  };
}

export function answer(game, choice) {
  if (!game || game.gameOver || !game.challenge || game.reveal) return game;
  const correct = choice === "heavier"
    ? measureValue(game.challenge) > measureValue(game.baseline)
    : measureValue(game.challenge) < measureValue(game.baseline);
  const answeredBaseline = game.baseline;
  if (correct) {
    game.streak += 1;
    game.baseline = game.challenge;
  } else {
    game.lives -= 1;
  }
  game.history.push({ correct });
  game.reveal = { correct, choice, item: game.challenge, baseline: answeredBaseline };
  return game;
}

export function nextRound(game) {
  if (!game || game.gameOver) return game;
  if (game.lives <= 0) {
    game.reveal = null;
    game.gameOver = true;
    return game;
  }
  game.round += 1;
  game.reveal = null;
  game.challenge = pickChallenge(game.items, game.baseline, game.used, game.round);
  game.used.add(game.challenge.id);
  return game;
}

export function emojiGrid(history, includeFinal = false) {
  const cells = history.map((entry) => (entry.correct ? "🟢" : "🔴"));
  if (includeFinal) cells.push("❌");
  return cells.join("");
}

function pickChallenge(items, baseline, used, round) {
  if (!items.length) return null;
  const sameMeasure = items.filter((item) => item.measureType === baseline.measureType);
  const available = sameMeasure.filter((item) => item.id !== baseline.id && !used.has(item.id) && !isNearEqual(measureValue(item), measureValue(baseline)));
  const pool = available.length ? available : sameMeasure.filter((item) => item.id !== baseline.id && !isNearEqual(measureValue(item), measureValue(baseline)));
  const hardRound = round % 5 < 2;
  const weighted = pool.filter((item) => {
    const ratio = measureValue(item) / measureValue(baseline);
    const close = ratio >= 0.3 && ratio <= 3;
    return hardRound ? close : !close;
  });
  const finalPool = weighted.length ? weighted : pool;
  return finalPool[Math.floor(Math.random() * finalPool.length)];
}

function isNearEqual(a, b) {
  return Math.abs(a - b) / Math.max(a, b) <= 0.01;
}
